import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Analytics.css';

const Analytics = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [analytics, setAnalytics] = useState(null);
  const [courseAnalytics, setCourseAnalytics] = useState([]);
  const [adminAnalytics, setAdminAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (isAdmin) {
          const response = await api.get(`/analytics/admin?period=${timeRange}`);
          setAdminAnalytics(response.data);
        } else {
          const [statsRes, coursesRes] = await Promise.all([
            api.get(`/analytics/student?days=${timeRange}`),
            api.get('/enrollments/my-courses'),
          ]);
          setAnalytics(statsRes.data);
          setCourseAnalytics(coursesRes.data);
        }
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [timeRange, isAdmin]);

  const getTotalStudyTime = () => {
    if (!analytics?.total_watch_time) return '0h 0m';
    const hours = Math.floor(analytics.total_watch_time / 3600);
    const minutes = Math.floor((analytics.total_watch_time % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getAverageDaily = () => {
    if (!analytics?.total_watch_time) return '0m';
    const days = parseInt(timeRange, 10);
    const avgMinutes = Math.floor(analytics.total_watch_time / 60 / days);
    return `${avgMinutes}m`;
  };

  const getCompletionRate = () => {
    if (!analytics || analytics.enrolled_courses === 0) return 0;
    return Math.round(
      (analytics.completed_courses / analytics.enrolled_courses) * 100
    );
  };

  const renderAdminAnalytics = () => {
    const stats = adminAnalytics?.stats || {};
    const dailyStats = adminAnalytics?.daily_stats || [];
    const topCourses = adminAnalytics?.top_courses || [];
    const usersByRole = adminAnalytics?.users_by_role || [];
    const maxEnrollments = Math.max(
      ...dailyStats.map((d) => Number(d.enrollments) || 0),
      1
    );
    const maxRevenue = Math.max(...dailyStats.map((d) => Number(d.revenue) || 0), 1);

    return (
      <>
        <section className="analytics-hero admin">
          <div className="hero-content">
            <p className="eyebrow">Platform Health</p>
            <h1>Analytics Overview</h1>
            <p>Monitor revenue, enrollments, and user mix at a glance.</p>
          </div>
          <div className="hero-controls">
            <label htmlFor="admin-range">Time range</label>
            <select
              id="admin-range"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
        </section>

        <div className="admin-analytics-grid">
          <div className="analytics-card admin key-stats">
            <h3>Key stats</h3>
            <div className="admin-stat-list">
              <div className="stat-badge">
                <span>Total users</span>
                <strong>{stats.total_users || 0}</strong>
                <small>{stats.new_users || 0} new</small>
              </div>
              <div className="stat-badge">
                <span>Published courses</span>
                <strong>{stats.total_courses || 0}</strong>
              </div>
              <div className="stat-badge">
                <span>Enrollments</span>
                <strong>{stats.total_enrollments || 0}</strong>
                <small>{stats.new_enrollments || 0} recent</small>
              </div>
              <div className="stat-badge">
                <span>Revenue</span>
                <strong>${Number(stats.total_revenue || 0).toFixed(2)}</strong>
                <small>${Number(stats.period_revenue || 0).toFixed(2)} period</small>
              </div>
            </div>
          </div>

          <div className="analytics-card admin daily-card">
            <h3>Daily performance</h3>
            {dailyStats.length === 0 ? (
              <p className="no-data">Need more data before trends appear.</p>
            ) : (
              <div className="daily-chart">
                {dailyStats.map((day) => (
                  <div key={day.date} className="chart-row">
                    <span className="chart-date">
                      {new Date(day.date).toLocaleDateString()}
                    </span>
                    <div className="chart-bars">
                      <div className="bar-track">
                        <div
                          className="bar enrollments"
                          style={{
                            width: `${(Number(day.enrollments) / maxEnrollments) * 100}%`,
                          }}
                        />
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar revenue"
                          style={{
                            width: `${(Number(day.revenue) / maxRevenue) * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="analytics-card admin full">
            <h3>Top courses</h3>
            {topCourses.length === 0 ? (
              <p className="no-data">No course data available.</p>
            ) : (
              <div className="top-course-list">
                {topCourses.map((course) => (
                  <div key={course.id} className="top-course-item">
                    <div>
                      <strong>{course.title}</strong>
                      <span>{course.enrollments} enrollments</span>
                    </div>
                    <span className="course-revenue">
                      ${Number(course.revenue || 0).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="analytics-card admin">
            <h3>User distribution</h3>
            {usersByRole.length === 0 ? (
              <p className="no-data">No role data available.</p>
            ) : (
              <div className="user-role-grid">
                {usersByRole.map((role) => (
                  <div key={role.role} className="role-stat">
                    <span>{role.role}</span>
                    <div className="role-meter">
                      <div
                        className="role-fill"
                        style={{
                          width: `${(Number(role.count) /
                            usersByRole.reduce(
                              (sum, entry) => sum + Number(entry.count),
                              0
                            )) * 100}%`,
                        }}
                      />
                    </div>
                    <strong>{role.count}</strong>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  if (loading) {
    return <div className="loading">Loading analytics...</div>;
  }

  if (isAdmin && adminAnalytics) {
    return (
      <div className="page analytics-page admin-view">
        <div className="container">{renderAdminAnalytics()}</div>
      </div>
    );
  }

  return (
    <div className="page analytics-page">
      <div className="container">
        <div className="analytics-header">
          <div>
            <h1>Learning Analytics</h1>
            <p>Track your learning progress and performance</p>
          </div>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="time-select"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
        </div>

        <div className="analytics-grid">
          <div className="analytics-card">
            <div className="card-icon study-time">
              <span>⏱️</span>
            </div>
            <div className="card-content">
              <h3>Total Study Time</h3>
              <p className="stat-value">{getTotalStudyTime()}</p>
              <p className="stat-sub">Avg. {getAverageDaily()}/day</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon courses">
              <span>📚</span>
            </div>
            <div className="card-content">
              <h3>Courses Enrolled</h3>
              <p className="stat-value">{analytics?.enrolled_courses || 0}</p>
              <p className="stat-sub">
                {analytics?.completed_courses || 0} completed
              </p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon videos">
              <span>🎬</span>
            </div>
            <div className="card-content">
              <h3>Videos Watched</h3>
              <p className="stat-value">{analytics?.videos_completed || 0}</p>
              <p className="stat-sub">lessons completed</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon completion">
              <span>🎯</span>
            </div>
            <div className="card-content">
              <h3>Completion Rate</h3>
              <p className="stat-value">{getCompletionRate()}%</p>
              <p className="stat-sub">courses finished</p>
            </div>
          </div>
        </div>

        <div className="analytics-section">
          <h2>Course Progress</h2>
          <div className="course-analytics-list">
            {courseAnalytics.length === 0 ? (
              <p className="no-data">No courses enrolled yet.</p>
            ) : (
              courseAnalytics.map((course) => {
                const progress =
                  course.total_videos > 0
                    ? Math.round(
                        (course.completed_videos / course.total_videos) * 100
                      )
                    : 0;

                return (
                  <div key={course.id} className="course-analytics-item">
                    <div className="course-info">
                      <h3>{course.title}</h3>
                      <p>{course.category_name}</p>
                    </div>
                    <div className="course-progress">
                      <div className="progress-info">
                        <span>
                          {course.completed_videos}/{course.total_videos} videos
                        </span>
                        <span className="progress-percent">{progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="analytics-section">
          <h2>Learning Habits</h2>
          <div className="habits-grid">
            <div className="habit-card">
              <h3>Best Streak</h3>
              <p className="habit-value">
                {analytics?.longest_streak || 0} <span>days</span>
              </p>
              <p className="habit-sub">consecutive learning days</p>
            </div>
            <div className="habit-card">
              <h3>Quizzes Taken</h3>
              <p className="habit-value">
                {analytics?.quizzes_taken || 0} <span>quizzes</span>
              </p>
              <p className="habit-sub">
                Avg. score: {analytics?.avg_quiz_score || 0}%
              </p>
            </div>
            <div className="habit-card">
              <h3>Certificates Earned</h3>
              <p className="habit-value">
                {analytics?.certificates_earned || 0} <span>certs</span>
              </p>
              <p className="habit-sub">course completions</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
